import type { JSX } from 'react';
import { PageLoader } from '@/components/common/PageLoader';
import { usePlanStore } from '@/stores';
import { isSubscriptionActive } from '@/lib/subscription';
import { LockedFeature } from './LockedFeature';

interface FeatureGateProps {
  /** Human-readable feature name, surfaced in the locked state. */
  feature: string;
  children: JSX.Element;
}

/**
 * Per-feature entitlement gate. Unlike the old app-wide SubscriptionGate, this
 * wraps only premium routes — the app shell and free pages render without it,
 * so a non-subscriber can browse. When the user lacks an active plan the real
 * page never mounts (its guarded API calls never fire); they see
 * [[LockedFeature]] with an upgrade CTA instead.
 *
 * Mirrors SubscriptionGate's two safety rules: never flash a lock before the
 * first subscription read resolves, and fail OPEN when that read errors (a
 * transient failure must not lock out a paying user — the BE still enforces
 * entitlement on the endpoint itself).
 */
export function FeatureGate({ feature, children }: FeatureGateProps) {
  const { subscription, subscriptionError, hydrated } = usePlanStore();

  // Wait for the first read so we never flash a lock to an entitled user.
  if (!hydrated) {
    return <PageLoader fullScreen={false} />;
  }

  // Fail open on a genuine read failure — same rationale as SubscriptionGate.
  if (subscriptionError && !isSubscriptionActive(subscription)) {
    return children;
  }

  if (!isSubscriptionActive(subscription)) {
    return <LockedFeature feature={feature} />;
  }

  return children;
}
